import React, { useCallback, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { apiService } from "../../../api/api";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Panel } from "primereact/panel";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import { trackToolAction } from "../../../utils/analytics";

interface linkingRow {
  musicVideoId: string;
  soundRecordingId: string;
}

const isValidRow = (row: string[]): boolean => {
  if (row.length !== 2) {
    return false;
  }
  const pattern = /^A\d{15}$/;
  return row.every((item: string) => pattern.test(item));
};

const separateRows = (rows: string[][]) => {
  const validRows: string[][] = [];
  const invalidRows: string[][] = [];

  rows.forEach((row) => {
    if (isValidRow(row)) {
      validRows.push(row);
    } else {
      invalidRows.push(row);
    }
  });

  return { validRows, invalidRows };
};

const LinkingTool: React.FC = () => {
  const [linkingRows, setLinkingRows] = useState<linkingRow[]>([]);
  const [invalidRows, setInvalidRows] = useState<string[][]>([]);
  const [musicVideoId, setMusicVideoId] = useState<string>("");
  const [soundRecordingId, setSoundRecordingId] = useState<string>("");
  const toast = useRef<Toast>(null);

  const handleCsvFileLoad = useCallback((data: string[][]) => {
    const { validRows, invalidRows } = separateRows(data);
    setLinkingRows(
      validRows.map((row: string[]) => ({
        musicVideoId: row[0],
        soundRecordingId: row[1],
      }))
    );
    setInvalidRows(invalidRows);
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        Papa.parse(file, {
          complete: (results) => {
            const data = results.data as string[][];
            const nonEmptyRows = data.filter(
              (row) =>
                Array.isArray(row) &&
                row.length > 0 &&
                row.some((cell) => cell && cell.trim() !== "")
            );
            handleCsvFileLoad(nonEmptyRows);
          },
          skipEmptyLines: true,
        });
      }
    },
    [handleCsvFileLoad]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
  });

  const handleLinkAssets = async (assets: linkingRow[]) => {
    if (assets.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No assets to link.",
        life: 3000,
      });
      return null;
    }
    try {
      const response = await apiService.mbm.linkMusicVideoAsset(assets);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: response,
        life: 3000,
      });
      return response;
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.toString(),
        life: 3000,
      });
      return null;
    }
  };

  const handleSingleLink = async () => {
    if (musicVideoId && soundRecordingId) {
      trackToolAction("SoundRecordingLinkingTool", "link_single");
      const response = await handleLinkAssets([
        { musicVideoId, soundRecordingId },
      ]);
      if (response) {
        setMusicVideoId("");
        setSoundRecordingId("");
      }
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please fill in all fields",
        life: 3000,
      });
    }
  };

  const handleBulkLink = async () => {
    trackToolAction("SoundRecordingLinkingTool", "link_bulk");
    const response = await handleLinkAssets(linkingRows);
    if (response) {
      setLinkingRows([]);
      setInvalidRows([]);
    }
  };

  return (
    <div>
      <Toast ref={toast}></Toast>
      <div className="grid">
        <div className="col-12 md:col-6">
          <Panel header="Manual Linking">
            <div className="p-fluid">
              <div className="field">
                <label htmlFor="musicVideoId">Music Video ID</label>
                <InputText
                  id="musicVideoId"
                  value={musicVideoId}
                  onChange={(e) => setMusicVideoId(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="soundRecordingId">Sound Recording ID</label>
                <InputText
                  id="soundRecordingId"
                  value={soundRecordingId}
                  onChange={(e) => setSoundRecordingId(e.target.value)}
                />
              </div>
              <Button
                label="Link Asset"
                icon="pi pi-link"
                onClick={handleSingleLink}
              />
            </div>
          </Panel>
        </div>

        <div className="col-12 md:col-6">
          <Panel header="Bulk Linking (CSV Upload)">
            <div className="p-fluid">
              <div
                {...getRootProps({
                  className: `p-4 border-2 border-dashed rounded-md text-center cursor-pointer ${
                    isDragActive
                      ? "border-primary bg-primary-reverse"
                      : "border-gray-300"
                  }`,
                })}
              >
                <input {...getInputProps()} />
                <i className="pi pi-upload text-2xl text-gray-500"></i>
                {isDragActive ? (
                  <p className="mt-2">Drop the CSV file here...</p>
                ) : (
                  <p className="mt-2">
                    Drag and drop a CSV file here, or click to select a file
                  </p>
                )}
              </div>

              {linkingRows.length > 0 && (
                <Button
                  label={`Link ${linkingRows.length} Assets`}
                  icon="pi pi-link"
                  onClick={handleBulkLink}
                  className="p-button-success mt-3"
                />
              )}
            </div>
          </Panel>
        </div>

        {(linkingRows.length > 0 || invalidRows.length > 0) && (
          <div className="col-12">
            <Panel header="CSV Data Preview" toggleable>
              {linkingRows.length > 0 && (
                <div>
                  <h5>Valid Rows ({linkingRows.length})</h5>
                  <DataTable
                    value={linkingRows}
                    paginator
                    rows={5}
                    dataKey="musicVideoId"
                    responsiveLayout="scroll"
                  >
                    <Column
                      field="musicVideoId"
                      header="Music Video ID"
                    ></Column>
                    <Column
                      field="soundRecordingId"
                      header="Sound Recording ID"
                    ></Column>
                  </DataTable>
                </div>
              )}
              {invalidRows.length > 0 && (
                <div className="mt-4">
                  <h5>Invalid Rows ({invalidRows.length})</h5>
                  <DataTable value={invalidRows} paginator rows={5}>
                    <Column field="0" header="Column 1"></Column>
                    <Column field="1" header="Column 2"></Column>
                  </DataTable>
                </div>
              )}
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkingTool;
